import { activeLevels, distributionJobStatus, formatAmount } from "@/lib/utils";
import { RootState } from "@/redux/store";
import { routes } from "@/routes/utils";
import { useGetCommunityFinancialInfo, useGetDistributionJobsGlDetails, useGetGroupGlListByGroupGlYear } from "@/services";
import { useEffect, useMemo, useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { RECEIVER, SOURCE_AMT, RUN_STATUS, CREATED_ON, RULE, RUN_ON, TABLE_DATA, TRIGGER_SOURCE, SOURCE } from "./consts";
import moment from "moment";
import { ColumnDef } from "@tanstack/react-table";
import { Button } from "@/components/ui";

const useViewJobGlDetails = () => {
  const { id, glId } = useParams();
  const communityData = useSelector((state: RootState) => state.currentData.communityData);
  const currentFinanceYear = useSelector((state: RootState) => state.currentData.currentFinanceYear);
  const distributionJobDtl = useSelector((state: RootState) => state.currentData.distributionJobDtl);
  const communityName = communityData?.name || communityData?.communityName || "";
  const [activeLevel, setActiveLevel] = useState<"ZONE" | "BLOCK" | "PROPERTY">("ZONE");
  const [openGroupGl, setOpenGroupGl] = useState<boolean>(false);
  const isSimulate = distributionJobDtl?.isSimulate;
  const isPlanned = distributionJobDtl?.budgetType?.toLowerCase() == "plan";
  const selectRowDocs = useRef<any>(null);
  const [openDocDialog, setOpenDocDialog] = useState<boolean>(false);

  const form = useForm<any>({
    defaultValues: {
      [SOURCE]: "",
      [RECEIVER]: "",
      [SOURCE_AMT]: "",
      [RUN_STATUS]: "",
      [RULE]: "",
      [TRIGGER_SOURCE]: "",
      [CREATED_ON]: "",
      [RUN_ON]: "",
      [TABLE_DATA]: [],
    }  
  });

  const tableData = useWatch({control: form.control, name: TABLE_DATA});

  const breadcrumbData = useMemo(
    () => [
      {
        link: `/${routes.admin}/${routes.activityDashboard}`,
        name: `Activity Dashboard - ${communityName}`,
      },
      {
        link: `/${routes.admin}/${routes.activityDashboard}/${routes.distributionRuntimeDetails}`,
        name: 'Allocation Run Time Details',
      },
      {
        link: `/${routes.admin}/${routes.activityDashboard}/${routes.distributionRuntimeDetails}/${id}`,
        name: isSimulate ? "Simulate Job" : `View Run Details (${id})`,
      },
      {
        link: "",
        name: <span className="text-primary">GL - {distributionJobDtl?.glAccountNumber}</span>,
      },
    ],
    [communityName]
  );

  const title = `GL - ${distributionJobDtl?.glAccountNumber} Details - ${communityName}(${currentFinanceYear})`;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);
  
  const {data: financialInfo, isLoading: isLoadingFinancialInfo } = useGetCommunityFinancialInfo({
    community_id: communityData?.communityId,
    queryParams: {
      enabled: !!(communityData?.communityId)
    }
  })

  const activeThresholdIndex = useMemo(() => activeLevels.findIndex(
    (level) => level.value == financialInfo?.data?.distributionLevel
  ), [financialInfo])

  const activeLevelIndex = useMemo(() => activeLevels.findIndex(
    (level) => level.value == activeLevel
  ), [activeLevel])

  useEffect(() => {
    setActiveLevel(distributionJobDtl?.receiverLevel?.toUpperCase() || "ZONE");
  }, [distributionJobDtl]);

  const {data: jobGlDtl, isLoading: isLoadingJobGl} = useGetDistributionJobsGlDetails({
    communityId: communityData?.communityId,
    jobId: id as string,
    glMasterId: glId as string,
    queryParams: {
      enabled: !!(id && glId && !isSimulate)
    }
  });

  const {data: groupGlList} = useGetGroupGlListByGroupGlYear({
    communityId: communityData?.communityId,
    year: currentFinanceYear,
    groupGlMasterId: glId as string,
    queryParams: {
      enabled: !!(communityData?.communityId && currentFinanceYear && glId)
    }
  });

  const jobGlDetails = isSimulate ? distributionJobDtl?.simulateDtl?.glDetails?.[glId as any] : jobGlDtl?.data;
  const groupGlMembers = distributionJobDtl?.budgetType?.toLowerCase() == "plan" ? groupGlList?.data?.planMembers : groupGlList?.data?.actualMembers;
  const isGroupGl = !!groupGlMembers?.length;

  const isZoneLevel = activeLevel == "ZONE";
  const isBlockLevel = activeLevel == "BLOCK";
  const isPropertyLevel = activeLevel == "PROPERTY";

  useEffect(() => {
    if (jobGlDetails) {
      const data = jobGlDetails;
      let tableData: any = [];

      if (isZoneLevel) {
        tableData = data?.communityToZone?.map((val: any) => ({...val, postDocuments: data?.postDocuments}) )
      } else if (isBlockLevel) {
        tableData = data?.communityToZone?.map((val: any) => ({...val, postDocuments: data?.postDocuments, sharePct: '100',
          children: data?.zoneToBlock
          ?.filter((block: any) => block?.parentId == val?.receiverId)
          ?.map((block: any) => ({...block, postDocuments: data?.postDocuments, sharePct: ((block?.distributedAmount * 100) / val?.distributedAmount), children: []}))
        }) )
      } else if (isPropertyLevel) {
        tableData = data?.communityToZone?.map((val: any) => ({...val, postDocuments: data?.postDocuments, sharePct: '100',
          children: data?.zoneToBlock?.filter((block: any) => block?.parentId == val?.receiverId)?.map((block: any) => ({...block, postDocuments: data?.postDocuments, sharePct: ((block?.distributedAmount * 100) / val?.distributedAmount),
            children: data?.blockToProperty?.filter((prop: any) => prop?.parentId == block?.receiverId)?.map((prop: any) => ({...prop, postDocuments: data?.postDocuments, sharePct: ((prop?.distributedAmount * 100) / block?.distributedAmount)}))
          }))
        }))
      }

      form.reset({
        [SOURCE]: activeLevels[activeLevelIndex]?.source || "",
        [RECEIVER]: activeLevels[activeLevelIndex]?.receiver || "",
        [SOURCE_AMT]: data?.[SOURCE_AMT] || "",
        [RUN_STATUS]: distributionJobStatus[data?.[RUN_STATUS]?.toLowerCase()]?.label || "",
        [RULE]: isZoneLevel ? data?.zoneRule : isBlockLevel ? data?.blockRule : isPropertyLevel ? data?.propertyRule : "",
        [TRIGGER_SOURCE]: data?.[TRIGGER_SOURCE] || "",
        [CREATED_ON]: data?.[CREATED_ON] ? moment(data?.[CREATED_ON]).format('MM/DD/YYYY') : "",
        [RUN_ON]: data?.[RUN_ON] ?  moment(data?.[RUN_ON]).format('MM/DD/YYYY') : "",
        [TABLE_DATA]: tableData,
      })
    }
  }, [jobGlDetails, activeLevel])

  const total = useMemo(() => {
    if (tableData) {
      let metricValue = 0, share = 0, amt = 0;

      tableData?.map((data: any) => {
        metricValue += Number(data?.metricValue || "0");
        share += Number(data?.sharePct || "0");
        amt += Number(data?.distributedAmount || "0");
      });

      return {metricValue, share: share.toFixed(2), amt: formatAmount(amt, 2)}
    }
    return {metricValue: 0, share: 0, amt: 0}
  }, [tableData]);

  // Helper for safe numeric formatting
  const formatNumber = (value: any, decimals = 2): string => {
    const num = Number(value);
    return isNaN(num) ? "0.00" : num.toFixed(decimals);
  };

  // Reusable Base Columns
  const receiverColumn = (headerLabel = ""): ColumnDef<any> => ({
    accessorKey: "receiverName",
    header: headerLabel,
    accessorFn: (row) => String(row?.receiverName || "-"),
  });

  const commonMetricsColumns: ColumnDef<any>[] = [
    {
      accessorKey: "metricValue",
      header: "Metric Value",
      accessorFn: (row) => String(row?.metricValue ?? "0"),
    },
    {
      accessorKey: "sharePct",
      header: "Share %",
      accessorFn: (row) => formatNumber(row?.sharePct),
    },
    {
      accessorKey: "distributedAmount",
      meta: { align: "right" },
      header: "Distributed Amount",
      accessorFn: (row) => formatAmount(row?.distributedAmount ?? "0", 2),
    },
  ];

  // Document & Line Item Specific Columns
  const documentColumns = [
    {
      accessorKey: "postDocuments",
      header: <div>Doc ID / Line ID</div>,
      cell: ({row}: any) => {
        return <Button variant="link" className="p-0" onClick={() => {
          selectRowDocs.current = row?.original?.postDocuments;
          setOpenDocDialog(true);
        }}>
          Link
        </Button>
      }
    }
  ];

  const columns = ({showReceiverHeader, showDocCols}: {showReceiverHeader: boolean, showDocCols: boolean}) => [
    receiverColumn(showReceiverHeader ? "Receiver" : ""),
    ...(showDocCols && !isPlanned ? documentColumns : []),
    ...commonMetricsColumns,
  ];

  const onLevelChange = (level: string) => () => {
    setActiveLevel(level as any);
  }
  
  const onOpenGlDialog = () => {
    setOpenGroupGl(true)
  }

  const docColumns = [
    {
      accessorKey: "documentNumber",
      header: <div>Doc ID</div>,
      accessorFn: (row: any) => String(row?.documentNumber || ""),
    },
    {
      accessorKey: "postDocumentLineItemIds",
      header: <div>Line ID(s)</div>,
      accessorFn: (row: any) => row?.postDocumentLineItemIds?.map((_: any, index: number) => index + 1)?.join(", "),
    },
  ];

  return {
    title,
    breadcrumbData,
    isLoadingFinancialInfo,
    activeThresholdIndex,
    isLoadingJobGl,
    form,
    columns,
    total,
    distributionJobDtl,
    onLevelChange,
    activeLevelIndex,
    isZoneLevel,
    isBlockLevel,
    isPropertyLevel,
    onOpenGlDialog,
    openGroupGl,
    setOpenGroupGl,
    groupGlMasterId: glId as string,
    isGroupGl,
    isSimulate,
    selectRowDocs: selectRowDocs.current,
    openDocDialog,
    setOpenDocDialog,
    docColumns
  }
}

export default useViewJobGlDetails;
